import { type IResponseContact } from 'entities/Contacts'
import { type IResponseTag } from 'entities/Tags'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type IResponseConversation } from 'entities/Conversation/api/types'

export type ContactSummary = {
  id: number
  name: string
  number: string
  email: string | null
  tags: string[]
}

export type ContactDetail = ContactSummary & {
  first_name: string | null
  last_name: string | null
  opted_out: boolean
  created_at: string
  notes_count: number
}

export const serializeContactSummary = (c: IResponseContact): ContactSummary => ({
  id: c.id,
  name: c.full_name,
  number: c.formatted_number || c.number,
  email: c.email,
  // Dedup by tag id: the backend franchise tag-attach path can create duplicate
  // pivot rows (no unique constraint), so guard the read side from surfacing dupes.
  tags: [...new Map((c.tags ?? []).map((t) => [t.id, t.label] as const)).values()],
})

export const serializeContactDetail = (c: IResponseContact): ContactDetail => ({
  ...serializeContactSummary(c),
  first_name: c.first_name,
  last_name: c.last_name,
  // opt_out is a status string ('hard'/'soft') when opted out, null otherwise
  // (mirrors the Contact model: opted-in iff opt_out === null).
  opted_out: c.opt_out !== null,
  created_at: c.created_at,
  notes_count: (c.notes ?? []).length,
})

export type TagSummary = { id: number; label: string }

export const serializeTag = (t: IResponseTag): TagSummary => ({
  id: t.id,
  label: t.label,
})

export type InboxSummary = {
  id: number
  name: string
  eligible: boolean
  number_id: number | null
}

export const serializeInbox = (i: IResponseInbox): InboxSummary => ({
  id: i.id,
  name: i.name,
  // Can send if it's an RCS sender or has at least one phone number attached.
  eligible:
    i.messaging_channel_type === 'rcs_sender' || Boolean(i.number) || (i.numbers?.length ?? 0) > 0,
  // For broadcast send_from; send_message uses the inbox id as team_id directly.
  number_id: i.number?.id ?? i.numbers?.[0]?.id ?? null,
})

export type ConversationSummary = {
  id: number
  contact: string
  unread_count: number
  last_message: string | null
}

export const serializeConversationSummary = (c: IResponseConversation): ConversationSummary => ({
  id: c.id,
  contact:
    c.contact?.full_name ||
    c.contact?.formatted_number ||
    c.contact?.number ||
    `Contact #${c.contact_id}`,
  unread_count: c.unread_count,
  last_message: c.recent_message?.body ?? null,
})
